import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, PaperAirplaneIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_CONFIG } from '../config/api';

// Sample queries for "Try asking about" suggestions
const SAMPLE_QUERIES = [
  'What does high cholesterol mean?',
  'Explain my CBC results',
  'What is an MRI?',
  'Drug interaction check',
  'How much sleep do I need?',
];

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('common:greeting.morning');
    if (hour < 18) return t('common:greeting.afternoon');
    return t('common:greeting.evening');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      return;
    }

    setLoading(true);
    setErrorMessage(null);

    try {
      // Call the classify endpoint
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CLASSIFY_QUERY), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: query.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to classify query');
      }

      const data = await response.json();
      const activity = data.activity;

      // Map activity to route (must match routes in App.tsx)
      const activityRoutes: Record<string, string> = {
        'Health Questions': '/health-questions',
        'Lab Results': '/lab-results',
        'Imaging Results': '/imaging-reports',
        'Medication Info': '/medication-info',
        'Symptom Tracker': '/symptom-tracker',
        'Health Metrics': '/health-metrics',
        'Medical Glossary': '/medical-glossary',
        'Exercise Tracker': '/exercise-tracker',
        'Insurance Guide': '/insurance-guide',
        'Health Records': '/health-records',
        'AI Advisor': '/ai-advisor',
      };

      const route = activityRoutes[activity];

      if (route) {
        // Navigate to the appropriate activity with context
        setRetryCount(0);
        navigate(route, { state: { initialQuery: query.trim() } });
      } else {
        // No appropriate activity found
        if (retryCount === 0) {
          // First attempt - ask user to be more specific
          setErrorMessage('I couldn\'t determine the best activity for your query. Please try being more specific about what you need help with.');
          setRetryCount(1);
        } else {
          // Second attempt - direct to sidebar
          setErrorMessage('I\'m still unable to find a matching activity. Please browse the menu in the sidebar to select the activity that best fits your needs.');
          setRetryCount(0);
        }
      }
    } catch (error) {
      console.error('Error classifying query:', error);
      setErrorMessage('An error occurred while processing your request. Please try again or select an activity from the sidebar.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion);
    setErrorMessage(null);
    setRetryCount(0);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full p-8">
      {/* Logo */}
      <div className="mb-8">
        <img src="/caduceus.png" alt="Auxilio Health" className="h-24 w-24" />
      </div>

      {/* Greeting */}
      <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-2">
        {getGreeting()}, {user?.username || 'there'}
      </h1>
      <p className="text-xl text-gray-600 dark:text-gray-400 mb-12">{t('common:dashboard.helpPrompt')}</p>

      {/* Search Bar */}
      <form onSubmit={handleSubmit} className="w-full max-w-3xl">
        <div className="relative flex items-center">
          <div className="absolute left-4">
            <MagnifyingGlassIcon className="h-6 w-6 text-gray-400" />
          </div>
          <input
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (errorMessage) setErrorMessage(null);
            }}
            placeholder="Ask a health question or describe what you need..."
            className="w-full pl-12 pr-12 py-4 text-lg border-2 border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:border-emerald-500 dark:focus:border-emerald-400 transition-colors bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <div className="absolute right-4 flex items-center space-x-2">
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="p-2 text-emerald-500 hover:text-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="mt-4 flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <ExclamationCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 dark:text-amber-200">{errorMessage}</p>
          </div>
        )}
      </form>

      {/* Try asking about suggestions */}
      <div className="mt-8 text-center w-full max-w-3xl">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">Try asking about:</p>
        <div className="flex flex-wrap justify-center gap-2">
          {SAMPLE_QUERIES.map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
